import {useTranslation} from 'react-i18next';
import {ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight} from 'lucide-react';
import {Button} from '../button';
import {cn} from '../lib/cn';
import {LegacySelect, LegacySelectItem} from '../legacy-fields/select';

export type PaginationVariant = 'default' | 'simple';

export type PaginationProps = {
  variant?: PaginationVariant;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pages: number;
  showPageSizeSelector?: boolean;
  page: number;
  pageSize?: number;
  totalItems?: number;
  className?: string;
};

const pageSizeOptions = [10, 20, 25, 30, 40, 50];

export function Pagination({
  page,
  pages,
  showPageSizeSelector = true,
  variant = 'default',
  onPageChange,
  pageSize = 20,
  onPageSizeChange,
  totalItems = 0,
  className,
}: PaginationProps) {
  const {t} = useTranslation();
  const isSimpleVariant = variant === 'simple';

  const startItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const goToFirstPage = () => onPageChange(1);
  const goToPreviousPage = () => onPageChange(Math.max(1, page - 1));
  const goToNextPage = () => onPageChange(Math.min(pages, page + 1));
  const goToLastPage = () => onPageChange(pages);

  const canGoPrevious = page > 1;
  const canGoNext = page < pages;
  const shouldShowPagination = pages > 1;

  const getInfoText = (): string => {
    if (isSimpleVariant) {
      return t('page_of_pages', {page, pages});
    }

    if (totalItems === 0) {
      return t('no_items_to_show');
    }

    return t('showing_range_of_total_items', {
      range: `${startItem}-${endItem}`,
      total: totalItems,
    });
  };

  if (totalItems === 0 || !shouldShowPagination) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-between gap-x-8 gap-y-1 px-2 sm:flex-row',
        className,
      )}
    >
      <div className="mr-auto flex-1 self-start font-medium text-[var(--chekin-color-gray-1)] sm:self-center">
        {getInfoText()}
      </div>
      <div className="ml-auto flex w-full items-center gap-8 sm:w-fit">
        {!isSimpleVariant && showPageSizeSelector && (
          <div className="hidden items-center gap-2 sm:flex">
            <p className="font-medium text-[var(--chekin-color-gray-1)]">
              {t('rows_per_page')}
            </p>
            <LegacySelect
              value={pageSize.toString()}
              onValueChange={value => onPageSizeChange(parseInt(value, 10))}
              triggerClassName="h-8 w-[67px]"
              containerClassName="w-[67px] gap-0"
            >
              {pageSizeOptions.map(size => (
                <LegacySelectItem key={size} value={size.toString()}>
                  {size}
                </LegacySelectItem>
              ))}
            </LegacySelect>
          </div>
        )}
        {!isSimpleVariant && (
          <div className="flex w-fit items-center justify-center font-medium">
            {t('page_of_pages', {page, pages})}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2 sm:ml-0">
          {!isSimpleVariant && (
            <Button
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={goToFirstPage}
              disabled={!canGoPrevious}
            >
              <span className="sr-only hidden">{t('go_to_first_page')}</span>
              <ChevronsLeft />
            </Button>
          )}
          <Button
            variant={isSimpleVariant ? 'link' : 'outline'}
            size="icon"
            className={cn('size-8', {
              '!text-[var(--chekin-color-gray-1)]': isSimpleVariant,
            })}
            onClick={goToPreviousPage}
            disabled={!canGoPrevious}
          >
            <span className="sr-only hidden">{t('go_to_previous_page')}</span>
            <ChevronLeft />
          </Button>
          <Button
            variant={isSimpleVariant ? 'link' : 'outline'}
            size="icon"
            className={cn('size-8', {
              '!text-[var(--chekin-color-gray-1)]': isSimpleVariant,
            })}
            onClick={goToNextPage}
            disabled={!canGoNext}
          >
            <span className="sr-only hidden">{t('go_to_next_page')}</span>
            <ChevronRight />
          </Button>
          {!isSimpleVariant && (
            <Button
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={goToLastPage}
              disabled={!canGoNext}
            >
              <span className="sr-only hidden">{t('go_to_last_page')}</span>
              <ChevronsRight />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
